/* RESPONSIBLE TEAM: team-app-security */
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

import type IntlService from 'ember-intl/services/intl';

import type {
  ScimGroup,
  Role,
  UserGroupMembership,
} from '../components/new-settings/workspace/teammates/scim/types';
import { ScimGroupState } from '../components/new-settings/workspace/teammates/scim/types';
import { SCIM_TRANSLATION_KEYS } from '../components/new-settings/workspace/teammates/scim/constants';
import { scimPriorityCompare } from '../components/new-settings/workspace/teammates/scim/utils';

export class ScimGroupsState {
  @tracked groups: ScimGroup[] = [];
  @tracked userGroups: UserGroupMembership[] = [];
  @tracked availableRoles: Role[] = [];

  @tracked roleChanges: Record<string, number | null> = {};
  @tracked customGroupOrder: string[] = [];
  @tracked includedRoleChanges: Record<string, boolean> = {};
  @tracked includeOrderChanges = true;

  constructor(
    groups: ScimGroup[] = [],
    userGroups: UserGroupMembership[] = [],
    availableRoles: Role[] = [],
    private baseState?: ScimGroupsState,
  ) {
    this.groups = groups;
    this.userGroups = userGroups;
    this.availableRoles = availableRoles;

    if (this.baseState) {
      this.resetToBaseState();
    } else {
      this.normalizePriorities();
    }
  }

  get sortedGroups(): ScimGroup[] {
    if (this.customGroupOrder.length === 0) {
      return [...this.groups].sort((a, b) => scimPriorityCompare(a, b));
    }

    return [...this.groups].sort((a, b) => {
      let aIndex = this.customGroupOrder.indexOf(a.id);
      let bIndex = this.customGroupOrder.indexOf(b.id);

      if (aIndex === -1 && bIndex === -1) {
        return scimPriorityCompare(a, b);
      }
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return -1;
      }

      return aIndex - bIndex;
    });
  }

  getTopPriorityGroupForUser(): UserGroupMembership | null {
    let topScimGroup = this.getTopPriorityScimGroupForUser();
    if (!topScimGroup || !topScimGroup.id) {
      return null;
    }

    return this.userGroups.find((userGroup) => userGroup.id.toString() === topScimGroup.id) || null;
  }

  getTopPriorityScimGroupForUser(): ScimGroup | null {
    if (!this.userGroups.length) {
      return null;
    }

    let userScimGroups = this.sortedGroups.filter((scimGroup) =>
      this.userGroups.some((userGroup) => userGroup.id.toString() === scimGroup.id),
    );

    return userScimGroups[0] || null;
  }

  getUserEffectiveRole(
    intl: IntlService,
  ): { roleName: string; groupName: string; groupId: string } | null {
    let topScimGroup = this.getTopPriorityScimGroupForUser();
    if (!topScimGroup || !topScimGroup.id) {
      return null;
    }

    let effectiveRoleId;
    if (this.baseState) {
      if (this.roleChanges[topScimGroup.id] !== undefined) {
        effectiveRoleId = this.roleChanges[topScimGroup.id];
      } else {
        effectiveRoleId = topScimGroup.role_id;
      }
    } else {
      effectiveRoleId = topScimGroup.role_id;
    }

    let role = this.availableRoles.find((r) => r.id === effectiveRoleId?.toString());
    let roleName = role?.name || intl.t(SCIM_TRANSLATION_KEYS.TAB.NO_ROLE_ASSIGNED);

    return {
      roleName,
      groupName: topScimGroup.name,
      groupId: topScimGroup.id,
    };
  }

  isUserInGroup(groupId: string): boolean {
    let groupIdInt = parseInt(groupId, 10);
    return this.userGroups.some((group) => group.id === groupIdInt);
  }

  isUserTopPriorityGroup(groupId: string): boolean {
    let topGroup = this.getTopPriorityGroupForUser();
    return topGroup?.id.toString() === groupId;
  }

  @action
  updateGroupRole(groupId: string, roleId: number | null): void {
    if (!this.baseState) {
      return;
    }

    let originalGroup = this.baseState.groups.find((g) => g.id === groupId);

    if (originalGroup?.role_id === roleId) {
      let { [groupId]: _removed, ...remaining } = this.roleChanges;
      this.roleChanges = remaining;
      let { [groupId]: _removedIncluded, ...remainingIncluded } = this.includedRoleChanges;
      this.includedRoleChanges = remainingIncluded;
    } else {
      this.roleChanges = { ...this.roleChanges, [groupId]: roleId };
      this.includedRoleChanges = { ...this.includedRoleChanges, [groupId]: true };
    }
  }

  @action
  updateGroupOrder(newOrder: string[]): void {
    this.customGroupOrder = [...newOrder];
  }

  @action
  normalizePriorities(): void {
    let activeGroups = this.groups.filter((group) => group.state !== ScimGroupState.INACTIVE);
    let inactiveGroups = this.groups.filter((group) => group.state === ScimGroupState.INACTIVE);

    let activePriorities = activeGroups
      .map((g) => g.priority)
      .filter((priority) => priority != null);
    let highestActivePriority = activePriorities.length > 0 ? Math.max(...activePriorities) : 0;

    inactiveGroups.forEach((group, index) => {
      group.priority = highestActivePriority + index + 1;
    });
  }

  @action
  resetToBaseState(): void {
    if (!this.baseState) {
      return;
    }

    this.roleChanges = {};
    this.includedRoleChanges = {};
    this.includeOrderChanges = true;
    this.normalizePriorities();
    this.customGroupOrder =
      this.baseState.customGroupOrder.length > 0
        ? [...this.baseState.customGroupOrder]
        : this.sortedGroups.map((g) => g.id);
  }

  hasChanges(): boolean {
    if (!this.baseState) {
      return false;
    }

    let hasRoleChanges = Object.keys(this.roleChanges).length > 0;
    let baseStateOrder = this.baseState.groups
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((g) => g.id);
    let hasOrderChanges = !this.arraysEqual(baseStateOrder, this.customGroupOrder);

    return hasRoleChanges || hasOrderChanges;
  }

  hasIncludedChanges(): boolean {
    if (!this.baseState) {
      return false;
    }

    let hasIncludedRoleChanges = Object.keys(this.roleChanges).length > 0;
    let baseStateOrder = this.baseState.groups
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((g) => g.id);
    let hasIncludedOrderChanges =
      this.includeOrderChanges && !this.arraysEqual(baseStateOrder, this.customGroupOrder);

    return hasIncludedRoleChanges || hasIncludedOrderChanges;
  }

  @action
  toggleRoleChangeInclusion(groupId: string): void {
    this.includedRoleChanges = {
      ...this.includedRoleChanges,
      [groupId]: !this.includedRoleChanges[groupId],
    };
  }

  @action
  toggleOrderChangesInclusion(): void {
    this.includeOrderChanges = !this.includeOrderChanges;
  }

  setupIncludedChanges(): void {
    if (!this.baseState) {
      return;
    }

    this.includedRoleChanges = Object.keys(this.roleChanges).reduce(
      (acc, groupId) => ({ ...acc, [groupId]: true }),
      {},
    );

    let baseStateOrder = this.baseState.groups
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map((g) => g.id);
    this.includeOrderChanges = !this.arraysEqual(baseStateOrder, this.customGroupOrder);
  }

  arraysEqual(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((val, i) => val === b[i]);
  }
}
